import React, { useState } from "react";
import {
  Plus,
  Calendar,
  Users,
  FileText,
  Vote,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit,
  Trash2,
  Eye,
  Download,
  MessageSquare,
  Upload,
  Search,
  Shield,
  UserCheck,
  BarChart3,
  Timer,
  Lock,
  Mail,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { Meeting } from "../context/DataContext";
import { useToast } from "../context/ToastContext";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Table from "../components/ui/Table";
import Modal from "../components/ui/Modal";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";

export default function Governance() {
  const { state, dispatch } = useData();
  const { showSuccess, showWarning } = useToast();
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "meetings" | "voting" | "documents" | "board-members"
  >("meetings");
  const [showVotingModal, setShowVotingModal] = useState(false);

  const handleAddMeeting = (meetingData: Omit<Meeting, "id">) => {
    const newMeeting: Meeting = {
      ...meetingData,
      id: Date.now().toString(),
    };
    dispatch({ type: "ADD_MEETING", payload: newMeeting });
    setShowAddModal(false);
  };

  const handleUpdateMeeting = (updatedMeeting: Meeting) => {
    dispatch({ type: "UPDATE_MEETING", payload: updatedMeeting });
    setShowMeetingModal(false);
  };

  const handleVote = (votingItemId: string, vote: "for" | "against") => {
    // Find the meeting that contains this voting item
    const meetingWithVotingItem = state.meetings.find((meeting) =>
      meeting.votingItems?.some((item) => item.id === votingItemId)
    );

    if (meetingWithVotingItem && meetingWithVotingItem.votingItems) {
      const updatedVotingItems = meetingWithVotingItem.votingItems.map(
        (item) => {
          if (item.id === votingItemId) {
            const currentVotes = item.votes || {};
            const currentVoteList = currentVotes[vote] || [];

            // Add current user to the vote list (in real app, use actual user ID)
            const userId = "current-user";
            if (!currentVoteList.includes(userId)) {
              currentVoteList.push(userId);
            }

            return {
              ...item,
              votes: {
                ...currentVotes,
                [vote]: currentVoteList,
              },
            };
          }
          return item;
        }
      );

      const updatedMeeting = {
        ...meetingWithVotingItem,
        votingItems: updatedVotingItems,
      };

      dispatch({ type: "UPDATE_MEETING", payload: updatedMeeting });
      showSuccess(
        "Vote Recorded",
        `Your vote "${vote}" has been recorded for the voting item.`
      );
    }
  };

  const createVotingItem = (
    title: string,
    description: string,
    deadline: string,
    meetingId?: string
  ) => {
    const newVotingItem = {
      id: Date.now().toString(),
      title,
      description,
      options: ["for", "against"],
      votes: {},
      status: "active" as const,
      deadline,
    };

    if (meetingId) {
      // Add to specific meeting
      const meeting = state.meetings.find((m) => m.id === meetingId);
      if (meeting) {
        const updatedMeeting = {
          ...meeting,
          votingItems: [...(meeting.votingItems || []), newVotingItem],
        };
        dispatch({ type: "UPDATE_MEETING", payload: updatedMeeting });
        showSuccess(
          "Voting Item Added",
          `Voting item "${title}" has been added to the meeting successfully.`
        );
      }
    } else {
      // Add to the most recent meeting or create a new one
      const latestMeeting = state.meetings[state.meetings.length - 1];
      if (latestMeeting) {
        const updatedMeeting = {
          ...latestMeeting,
          votingItems: [...(latestMeeting.votingItems || []), newVotingItem],
        };
        dispatch({ type: "UPDATE_MEETING", payload: updatedMeeting });
        showSuccess(
          "Voting Item Added",
          `Voting item "${title}" has been added to the latest meeting successfully.`
        );
      } else {
        showWarning(
          "No Meetings Available",
          "No meetings are available to add voting items to. Please create a meeting first."
        );
      }
    }
  };

  // Board members data
  const boardMembers = [
    {
      id: "1",
      name: "Captain Sarah Ahmed",
      position: "Chairperson",
      email: "sarah.ahmed@syndicate.eg",
      phone: "+20 100 123 4567",
      termStart: "2023-01-01",
      termEnd: "2025-12-31",
      status: "active",
      responsibilities: [
        "Overall leadership",
        "External relations",
        "Strategic planning",
      ],
    },
    {
      id: "2",
      name: "Captain Mohamed Hassan",
      position: "Vice Chairperson",
      email: "mohamed.hassan@syndicate.eg",
      phone: "+20 100 234 5678",
      termStart: "2023-01-01",
      termEnd: "2025-12-31",
      status: "active",
      responsibilities: [
        "Internal operations",
        "Member relations",
        "Committee coordination",
      ],
    },
    {
      id: "3",
      name: "Captain Fatma Ibrahim",
      position: "Treasurer",
      email: "fatma.ibrahim@syndicate.eg",
      phone: "+20 100 345 6789",
      termStart: "2023-01-01",
      termEnd: "2025-12-31",
      status: "active",
      responsibilities: [
        "Financial management",
        "Budget oversight",
        "Financial reporting",
      ],
    },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "scheduled":
        return <Badge variant="info">Scheduled</Badge>;
      case "in-progress":
        return <Badge variant="warning">In Progress</Badge>;
      case "completed":
        return <Badge variant="success">Completed</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "board":
        return <Badge variant="success">Board</Badge>;
      case "committee":
        return <Badge variant="info">Committee</Badge>;
      case "general":
        return <Badge variant="warning">General</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const meetingColumns = [
    {
      key: "title",
      header: "Meeting",
      render: (meeting: Meeting) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {meeting.title}
          </div>
          <div className="text-sm text-gray-500">{meeting.type}</div>
        </div>
      ),
    },
    {
      key: "date",
      header: "Date",
      render: (meeting: Meeting) => format(new Date(meeting.date), "dd/MM/yy"),
    },
    {
      key: "type",
      header: "Type",
      render: (meeting: Meeting) => getTypeBadge(meeting.type),
    },
    {
      key: "status",
      header: "Status",
      render: (meeting: Meeting) => getStatusBadge(meeting.status),
    },
    {
      key: "attendees",
      header: "Attendees",
      render: (meeting: Meeting) => `${meeting.attendees.length} members`,
    },
    {
      key: "actions",
      header: "Actions",
      render: (meeting: Meeting) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedMeeting(meeting);
              setShowMeetingModal(true);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedMeeting(meeting);
              setShowMeetingModal(true);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Extract voting items from meetings
  const votingItems = state.meetings
    .flatMap((meeting) => meeting.votingItems || [])
    .map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      status: item.status,
      votesFor: item.votes?.for?.length || 0,
      votesAgainst: item.votes?.against?.length || 0,
      totalVotes:
        (item.votes?.for?.length || 0) + (item.votes?.against?.length || 0),
      deadline: item.deadline || "",
    }));

  const documents = [
    {
      id: "1",
      title: "Syndicate Constitution 2024",
      type: "Constitution",
      lastModified: "2024-01-15",
      size: "2.3 MB",
    },
    {
      id: "2",
      title: "Board Meeting Minutes - January 2024",
      type: "Minutes",
      lastModified: "2024-01-20",
      size: "1.1 MB",
    },
    {
      id: "3",
      title: "Financial Regulations",
      type: "Regulation",
      lastModified: "2024-01-10",
      size: "856 KB",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Governance & Board Support
          </h1>
          <p className="text-gray-600">
            Manage meetings, voting, and governance documents
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Schedule Meeting
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Meetings
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {state.meetings.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.meetings.filter((m) => m.status === "completed").length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Scheduled</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.meetings.filter((m) => m.status === "scheduled").length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Vote className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Votes</p>
              <p className="text-2xl font-bold text-gray-900">
                {votingItems.filter((v) => v.status === "active").length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "meetings", name: "Meetings", icon: Calendar },
            { id: "voting", name: "Voting", icon: Vote },
            { id: "documents", name: "Documents", icon: FileText },
            { id: "board-members", name: "Board Members", icon: UserCheck },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "meetings" && (
        <Card padding="none">
          <Table
            data={state.meetings}
            columns={meetingColumns}
            onRowClick={(meeting) => {
              setSelectedMeeting(meeting);
              setShowMeetingModal(true);
            }}
            emptyMessage="No meetings scheduled"
          />
        </Card>
      )}

      {activeTab === "voting" && (
        <div className="space-y-6">
          {/* Voting Actions */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Active Voting Items
              </h3>
              <Button onClick={() => setShowVotingModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Voting Item
              </Button>
            </div>
            <div className="space-y-4">
              {votingItems.map((item) => (
                <div
                  key={item.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 mt-1">{item.description}</p>
                      <div className="flex items-center mt-3 space-x-6">
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                          <span className="text-sm text-gray-600">
                            {item.votesFor} For
                          </span>
                        </div>
                        <div className="flex items-center">
                          <AlertCircle className="h-4 w-4 text-red-500 mr-1" />
                          <span className="text-sm text-gray-600">
                            {item.votesAgainst} Against
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Timer className="h-4 w-4 text-blue-500 mr-1" />
                          <span className="text-sm text-gray-500">
                            Deadline:{" "}
                            {format(new Date(item.deadline), "dd/MM/yy")}
                          </span>
                        </div>
                      </div>
                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Voting Progress</span>
                          <span>{item.totalVotes} votes</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full"
                            style={{
                              width: `${Math.min(
                                (item.totalVotes / state.members.length) * 100,
                                100
                              )}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      {item.status === "active" ? (
                        <Badge variant="warning">Active</Badge>
                      ) : (
                        <Badge variant="success">Completed</Badge>
                      )}
                    </div>
                  </div>
                  {item.status === "active" && (
                    <div className="mt-4 flex space-x-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVote(item.id, "for")}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Vote For
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVote(item.id, "against")}
                      >
                        <AlertCircle className="h-4 w-4 mr-1" />
                        Vote Against
                      </Button>
                      <Button variant="ghost" size="sm">
                        <BarChart3 className="h-4 w-4 mr-1" />
                        View Results
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {activeTab === "documents" && (
        <div className="space-y-6">
          {/* Document Management */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Governance Documents
              </h3>
              <div className="flex gap-2">
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
                <Button variant="outline">
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Modified
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 text-gray-400 mr-3" />
                          <div className="text-sm font-medium text-gray-900">
                            {doc.title}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="info">{doc.type}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(doc.lastModified), "dd/MM/yy")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {doc.size}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Lock className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-600">
                            Board Only
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {activeTab === "board-members" && (
        <div className="space-y-6">
          {/* Board Members Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {boardMembers.map((member) => (
              <Card key={member.id}>
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <UserCheck className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {member.name}
                  </h3>
                  <p className="text-primary-600 font-medium">
                    {member.position}
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2" />
                      {member.email}
                    </div>
                    <div className="flex items-center justify-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      Term: {format(new Date(member.termStart), "yyyy")} -{" "}
                      {format(new Date(member.termEnd), "yyyy")}
                    </div>
                  </div>
                  <div className="mt-4">
                    <Badge
                      variant={
                        member.status === "active" ? "success" : "default"
                      }
                    >
                      {member.status}
                    </Badge>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Responsibilities
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {member.responsibilities.map((responsibility, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-primary-500 mr-2">•</span>
                        {responsibility}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-4 flex space-x-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Contact
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    <Eye className="h-4 w-4 mr-1" />
                    Profile
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Board Structure */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Board Structure & Hierarchy
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-primary-50 rounded-lg">
                <div className="flex items-center">
                  <Shield className="h-6 w-6 text-primary-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Chairperson</h4>
                    <p className="text-sm text-gray-600">Captain Sarah Ahmed</p>
                  </div>
                </div>
                <Badge variant="success">Executive</Badge>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Users className="h-6 w-6 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Vice Chairperson
                    </h4>
                    <p className="text-sm text-gray-600">
                      Captain Mohamed Hassan
                    </p>
                  </div>
                </div>
                <Badge variant="info">Executive</Badge>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <BarChart3 className="h-6 w-6 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Treasurer</h4>
                    <p className="text-sm text-gray-600">
                      Captain Fatma Ibrahim
                    </p>
                  </div>
                </div>
                <Badge variant="info">Executive</Badge>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Meeting Details Modal */}
      {selectedMeeting && (
        <MeetingDetailsModal
          meeting={selectedMeeting}
          isOpen={showMeetingModal}
          onClose={() => {
            setShowMeetingModal(false);
            setSelectedMeeting(null);
          }}
          onUpdate={handleUpdateMeeting}
        />
      )}

      {/* Add Meeting Modal */}
      <AddMeetingModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddMeeting}
      />

      {/* Create Voting Item Modal */}
      <CreateVotingItemModal
        isOpen={showVotingModal}
        onClose={() => setShowVotingModal(false)}
        onCreate={createVotingItem}
      />
    </div>
  );
}

// Meeting Details Modal Component
function MeetingDetailsModal({
  meeting,
  isOpen,
  onClose,
  onUpdate,
}: {
  meeting: Meeting;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (meeting: Meeting) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Meeting Details" size="lg">
      <div className="space-y-6">
        {/* Meeting Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{meeting.title}</h3>
            <div className="flex items-center mt-2 space-x-4">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                <span className="text-sm text-gray-600">
                  {format(new Date(meeting.date), "EEEE, dd/MM/yy")}
                </span>
              </div>
              <Badge
                variant={
                  meeting.status === "completed"
                    ? "success"
                    : meeting.status === "in-progress"
                    ? "warning"
                    : "info"
                }
              >
                {meeting.status}
              </Badge>
            </div>
          </div>
        </div>

        {/* Agenda */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Agenda</h4>
          <div className="space-y-2">
            {meeting.agenda.map((item, index) => (
              <div
                key={index}
                className="flex items-center p-3 bg-gray-50 rounded-lg"
              >
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                  {index + 1}
                </span>
                <span className="text-sm text-gray-900">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Attendees */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Attendees</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {meeting.attendees.map((attendee, index) => (
              <div
                key={index}
                className="flex items-center p-3 bg-gray-50 rounded-lg"
              >
                <Users className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-900">{attendee}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Minutes */}
        {meeting.minutes && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Minutes</h4>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{meeting.minutes}</p>
            </div>
          </div>
        )}

        {/* Resolutions */}
        {meeting.resolutions && meeting.resolutions.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Resolutions</h4>
            <div className="space-y-2">
              {meeting.resolutions.map((resolution, index) => (
                <div
                  key={index}
                  className="flex items-start p-3 bg-green-50 rounded-lg"
                >
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                  <span className="text-sm text-gray-900">{resolution}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => setIsEditing(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Meeting
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Add Meeting Modal Component
function AddMeetingModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (meeting: Omit<Meeting, "id">) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    type: "board" as const,
    status: "scheduled" as const,
    agenda: [] as string[],
    attendees: [] as string[],
  });

  const [newAgendaItem, setNewAgendaItem] = useState("");
  const [newAttendee, setNewAttendee] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({
      title: "",
      date: new Date().toISOString().split("T")[0],
      type: "board",
      status: "scheduled",
      agenda: [],
      attendees: [],
    });
  };

  const addAgendaItem = () => {
    if (newAgendaItem.trim()) {
      setFormData({
        ...formData,
        agenda: [...formData.agenda, newAgendaItem.trim()],
      });
      setNewAgendaItem("");
    }
  };

  const addAttendee = () => {
    if (newAttendee.trim()) {
      setFormData({
        ...formData,
        attendees: [...formData.attendees, newAttendee.trim()],
      });
      setNewAttendee("");
    }
  };

  const removeAgendaItem = (index: number) => {
    setFormData({
      ...formData,
      agenda: formData.agenda.filter((_, i) => i !== index),
    });
  };

  const removeAttendee = (index: number) => {
    setFormData({
      ...formData,
      attendees: formData.attendees.filter((_, i) => i !== index),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Schedule New Meeting"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Meeting Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meeting Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="board">Board Meeting</option>
              <option value="committee">Committee Meeting</option>
              <option value="general">General Assembly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="scheduled">Scheduled</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Agenda Items */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Agenda Items
          </label>
          <div className="flex gap-2 mb-3">
            <Input
              value={newAgendaItem}
              onChange={(e) => setNewAgendaItem(e.target.value)}
              placeholder="Add agenda item"
              className="flex-1"
            />
            <Button type="button" onClick={addAgendaItem}>
              Add
            </Button>
          </div>
          <div className="space-y-2">
            {formData.agenda.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-900">{item}</span>
                <button
                  type="button"
                  onClick={() => removeAgendaItem(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Attendees */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Attendees
          </label>
          <div className="flex gap-2 mb-3">
            <Input
              value={newAttendee}
              onChange={(e) => setNewAttendee(e.target.value)}
              placeholder="Add attendee"
              className="flex-1"
            />
            <Button type="button" onClick={addAttendee}>
              Add
            </Button>
          </div>
          <div className="space-y-2">
            {formData.attendees.map((attendee, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-900">{attendee}</span>
                <button
                  type="button"
                  onClick={() => removeAttendee(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Schedule Meeting</Button>
        </div>
      </form>
    </Modal>
  );
}

// Create Voting Item Modal Component
function CreateVotingItemModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (title: string, description: string, deadline: string) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate(formData.title, formData.description, formData.deadline);
    setFormData({
      title: "",
      description: "",
      deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Voting Item"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Voting Item Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Budget Approval for Q2 2024"
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Provide detailed description of the voting item..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={4}
            required
          />
        </div>

        <Input
          label="Voting Deadline"
          type="date"
          value={formData.deadline}
          onChange={(e) =>
            setFormData({ ...formData, deadline: e.target.value })
          }
          required
        />

        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">
            Voting Guidelines
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Only board members can vote on this item</li>
            <li>• Voting will be anonymous and secure</li>
            <li>• Results will be published after the deadline</li>
            <li>• A simple majority (50% + 1) is required for approval</li>
          </ul>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            <Vote className="h-4 w-4 mr-2" />
            Create Voting Item
          </Button>
        </div>
      </form>
    </Modal>
  );
}
